import { genericMemo } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import {
  RedoListItem,
  TextRedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { useMemo } from "react";

export const OperatorSelector = genericMemo(function OperatorSelector<
  T extends string,
>({
  availableOptions,
  onOperatorSelected,
  selectedOperator,
  operatorToText,
  fitToAnchor = false,
  flexGrow = false,
  readonly = false,
}: {
  availableOptions: readonly T[];
  onOperatorSelected: (operator: T) => void;
  selectedOperator: T | null;
  operatorToText: Record<T, string>;
  fitToAnchor?: boolean;
  flexGrow?: boolean;
  readonly?: boolean;
}) {
  const operatorOptions: TextRedoListItem<T>[] = useMemo(() => {
    return availableOptions.map((operator) => ({
      type: "text",
      id: operator,
      value: operator,
      text: operatorToText[operator],
    }));
  }, [availableOptions, operatorToText]);

  const selectedItem = useMemo(() => {
    return operatorOptions.find((option) => option.value === selectedOperator);
  }, [operatorOptions, selectedOperator]);

  const handleOperatorSelected = useHandler(
    ({ item }: { item: RedoListItem<T> }) => {
      onOperatorSelected(item.value);
    },
  );

  return (
    <RedoSingleSelectDropdownInput<T>
      disabled={readonly}
      fitToAnchor={fitToAnchor}
      flexGrow={flexGrow}
      options={operatorOptions}
      optionSelected={handleOperatorSelected}
      selectedItem={selectedItem}
    />
  );
});
